"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EquipmentService, {
  EQUIPMENT_STATUS,
  EQUIPMENT_STATUS_NAME,
  EQUIPMENT_TYPE,
  EQUIPMENT_TYPE_NAME,
  Equipment,
} from "../services/equipment-service";
import EquipmentTable, { EquipmentRow } from "../components/equipment-table";
import EquipmentCreateModal, { EquipmentFormValues } from "../components/equipment-create-modal";
import EquipmentDetailModal from "../components/equipment-detail-modal";
import EquipmentQRModal from "../components/equipment-qr-modal";
import EquipmentImportModal from "../components/equipment-import-modal";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
import BranchService, { Branch } from "../../branch/services/branch-service";
import socketService, { SOCKET_EMIT_EVENTS, OrderEventData } from "../../../services/socket.service";

function useDebounce<T>(value: T, delay = 500): T {
  const [val, setVal] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setVal(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return val;
}

/** Extract array from API response that may be nested: array | { list: [] } | { data: [] } */
function extractList(raw: any): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const arr = raw.list ?? raw.data;
    if (Array.isArray(arr)) return arr;
  }
  return [];
}

const EquipmentIndexPage: React.FC = () => {
  // State
  const [rows, setRows] = useState<EquipmentRow[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number | undefined>();
  const [filterType, setFilterType] = useState<number | undefined>();
  const [filterStatus, setFilterStatus] = useState<number | undefined>();
  const [keySearch, setKeySearch] = useState("");
  const debouncedSearch = useDebounce(keySearch, 500);

  // Modals
  const [openCreate, setOpenCreate] = useState(false);
  const [editRow, setEditRow] = useState<EquipmentRow | null>(null);
  const [detailRow, setDetailRow] = useState<EquipmentRow | null>(null);
  const [qrRow, setQrRow] = useState<EquipmentRow | null>(null);
  const [openImport, setOpenImport] = useState(false);

  // Services
  const { fetch: listEquipments, loading: loadingList } = EquipmentService.list();
  const { fetch: createEquipment, loading: creating } = EquipmentService.create();
  const { fetch: updateEquipment, loading: updating } = EquipmentService.update();
  const { fetch: releaseEquipment } = EquipmentService.release();
  const { fetch: removeEquipment } = EquipmentService.remove();
  const { fetch: listBranches } = BranchService.list();

  const listRef = useRef(listEquipments);
  useEffect(() => { listRef.current = listEquipments; }, [listEquipments]);

  const branchListRef = useRef(listBranches);
  useEffect(() => { branchListRef.current = listBranches; }, [listBranches]);

  // Load branches
  useEffect(() => {
    (async () => {
      const res = await branchListRef.current({ status: 1, company_id: 2000 });
      if (res && isSuccess(res)) {
        const branchList = extractList(res.data);
        setBranches(branchList);
        if (branchList.length > 0 && !selectedBranchId) {
          setSelectedBranchId(branchList[0].id);
        }
      }
    })();
  }, []);

  // Load equipments
  const load = useCallback(async () => {
    if (!selectedBranchId || selectedBranchId <= 0) return;
    const res = await listRef.current({
      branch_id: selectedBranchId,
      type: filterType,
      status: filterStatus,
      key_search: debouncedSearch || undefined,
    });
    if (res && isSuccess(res)) {
      const list = extractList(res.data);
      setRows(
        list.map((e: Equipment) => ({
          id: e.id,
          code: e.code,
          name: e.name,
          type: e.type,
          status: e.status,
          barcode_data: e.barcode_data,
          current_order_id: e.current_order_id,
          capacity: e.capacity,
          note: e.note,
          created_at: e.created_at,
        }))
      );
    }
  }, [selectedBranchId, filterType, filterStatus, debouncedSearch]);

  useEffect(() => { void load(); }, [load]);

  // Realtime: tự động refresh khi có sự kiện gắn/trả thiết bị
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    const handleEquipmentChange = (data: OrderEventData) => {
      // Chỉ refresh nếu event thuộc branch đang chọn
      if (selectedBranchId && data.branch_id === selectedBranchId) {
        console.log('[Equipment Realtime] Equipment changed, refreshing list...');
        loadRef.current();
      }
    };

    socketService.on(SOCKET_EMIT_EVENTS.EQUIPMENT_ASSIGNED, handleEquipmentChange);
    socketService.on(SOCKET_EMIT_EVENTS.EQUIPMENT_RELEASED, handleEquipmentChange);

    return () => {
      socketService.off(SOCKET_EMIT_EVENTS.EQUIPMENT_ASSIGNED, handleEquipmentChange);
      socketService.off(SOCKET_EMIT_EVENTS.EQUIPMENT_RELEASED, handleEquipmentChange);
    };
  }, [selectedBranchId]);

  // Counts by status
  const countByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    rows.forEach((r) => {
      counts[r.status] = (counts[r.status] || 0) + 1;
    });
    return counts;
  }, [rows]);

  // Kiểm tra chi nhánh hợp lệ (phải là số dương)
  const isValidBranch = selectedBranchId !== undefined && selectedBranchId > 0;

  // Handlers
  const onCreate = async (values: EquipmentFormValues): Promise<boolean> => {
    if (!isValidBranch) {
      showToast({ message: "Vui lòng chọn chi nhánh trước khi tạo thiết bị", type: "error" });
      return false;
    }
    try {
      const payload = {
        branch_id: selectedBranchId,
        code: values.code,
        name: values.name || values.code,
        type: Number(values.type),
        capacity: values.capacity ? Number(values.capacity) : undefined,
        note: values.note,
      };
      const res = await createEquipment(payload as any);
      if (res && isSuccess(res)) {
        showToast({ message: "Tạo thiết bị thành công", type: "success" });
        void load();
        return true;
      }
      showToast({ message: res?.message || "Tạo thiết bị thất bại", type: "error" });
      return false;
    } catch (err) {
      console.error("[onCreate] Error:", err);
      showToast({ message: "Lỗi khi tạo thiết bị", type: "error" });
      return false;
    }
  };

  const onUpdate = async (values: EquipmentFormValues): Promise<boolean> => {
    if (!editRow) return false;
    try {
      const res = await updateEquipment(editRow.id, {
        name: values.name,
        type: Number(values.type),
        status: values.status ? Number(values.status) : undefined,
        capacity: values.capacity ? Number(values.capacity) : undefined,
        note: values.note,
      } as any);
      if (res && isSuccess(res)) {
        showToast({ message: "Cập nhật thiết bị thành công", type: "success" });
        void load();
        return true;
      }
      showToast({ message: res?.message || "Cập nhật thất bại", type: "error" });
      return false;
    } catch (err) {
      console.error("[onUpdate] Error:", err);
      showToast({ message: "Lỗi khi cập nhật thiết bị", type: "error" });
      return false;
    }
  };

  const onRelease = async (row: EquipmentRow) => {
    if (!window.confirm(`Xác nhận trả thiết bị "${row.code}"?`)) return;
    const res = await releaseEquipment(row.id);
    if (res && isSuccess(res)) {
      showToast({ message: "Trả thiết bị thành công", type: "success" });
      void load();
    } else {
      showToast({ message: res?.message || "Trả thiết bị thất bại", type: "error" });
    }
  };

  const onDelete = async (row: EquipmentRow) => {
    if (!window.confirm(`Xóa thiết bị "${row.code}"? Chỉ xóa được khi thiết bị đang trống và chưa từng dùng cho đơn nào.`)) return;
    const res = await removeEquipment(row.id);
    if (res && isSuccess(res)) {
      showToast({ message: "Đã xóa thiết bị", type: "success" });
      void load();
    } else {
      showToast({ message: res?.message || "Xóa thiết bị thất bại", type: "error" });
    }
  };

  return (
    <div className="space-y-6">
      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-green-600 tracking-tight">
            Quản lý thiết bị
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Quản lý túi giặt, sọt, máy giặt, máy sấy, bàn ủi, bàn gấp
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (!isValidBranch) {
                showToast({ message: "Vui lòng chọn chi nhánh trước", type: "error" });
                return;
              }
              setOpenImport(true);
            }}
            disabled={!isValidBranch}
            className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold shadow-lg transition-all ${
              isValidBranch
                ? "bg-white border-2 border-green-600 text-green-700 hover:bg-green-50 cursor-pointer"
                : "bg-gray-100 border-2 border-gray-300 text-gray-400 cursor-not-allowed opacity-60"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            <span>IMPORT EXCEL</span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (!isValidBranch) {
                showToast({ message: "Vui lòng chọn chi nhánh trước", type: "error" });
                return;
              }
              setEditRow(null);
              setOpenCreate(true);
            }}
            disabled={!isValidBranch}
            className={`group inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold text-white shadow-lg transition-all ${
              isValidBranch
                ? "bg-green-600 shadow-green-200 hover:bg-green-700 hover:-translate-y-0.5 cursor-pointer"
                : "bg-gray-400 shadow-gray-200 cursor-not-allowed opacity-60"
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
              <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
            </svg>
            <span>THÊM THIẾT BỊ</span>
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT CARD ── */}
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">

        {/* ── TOOLBAR (FILTERS & SEARCH) ── */}
        <div className="px-6 py-5 border-b border-gray-100 flex flex-col xl:flex-row xl:items-center justify-between gap-5">

          {/* Filter Group */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Branch Selector */}
            <select
              value={selectedBranchId ?? ""}
              onChange={(e) => {
                const val = Number(e.target.value);
                setSelectedBranchId(val > 0 ? val : undefined);
              }}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer"
            >
              {branches.length === 0 && (
                <option value="">Đang tải chi nhánh...</option>
              )}
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>

            {/* Type Filter */}
            <select
              value={filterType ?? ""}
              onChange={(e) => setFilterType(e.target.value ? Number(e.target.value) : undefined)}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer"
            >
              <option value="">Tất cả loại</option>
              {Object.entries(EQUIPMENT_TYPE_NAME).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus ?? ""}
              onChange={(e) => setFilterStatus(e.target.value ? Number(e.target.value) : undefined)}
              className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all cursor-pointer"
            >
              <option value="">Tất cả trạng thái</option>
              {Object.entries(EQUIPMENT_STATUS_NAME).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-[320px]">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              value={keySearch}
              onChange={(e) => setKeySearch(e.target.value)}
              placeholder="Tìm theo mã, tên thiết bị..."
              className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10 transition-all"
            />
          </div>
        </div>

        {/* ── STATUS SUMMARY CHIPS ── */}
        <div className="px-6 py-3 border-b border-gray-50 bg-gray-50/30 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tổng quan:</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
            Khả dụng: {countByStatus[EQUIPMENT_STATUS.AVAILABLE] || 0}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1 text-xs font-bold text-blue-700">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
            Đang dùng: {countByStatus[EQUIPMENT_STATUS.IN_USE] || 0}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-yellow-50 px-3 py-1 text-xs font-bold text-yellow-700">
            <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
            Bảo trì: {countByStatus[EQUIPMENT_STATUS.MAINTENANCE] || 0}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-bold text-red-700">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            Ngưng: {countByStatus[EQUIPMENT_STATUS.DECOMMISSIONED] || 0}
          </span>
        </div>

        {/* ── TABLE AREA ── */}
        <div className="p-0">
          <EquipmentTable
            rows={rows}
            isLoading={loadingList}
            startIndex={1}
            onEdit={(row) => { setEditRow(row); setOpenCreate(true); }}
            onViewDetail={(row) => setDetailRow(row)}
            onRelease={onRelease}
            onShowQR={(row) => setQrRow(row)}
            onDelete={onDelete}
          />
        </div>

        {/* ── FOOTER ── */}
        <div className="border-t border-gray-100 bg-gray-50/50 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 font-medium">
            {rows.length > 0
              ? `Tổng cộng ${rows.length} thiết bị`
              : "Chưa có thiết bị nào"}
          </div>
        </div>
      </div>

      {/* ── MODALS ── */}
      <EquipmentCreateModal
        isOpen={openCreate}
        onClose={() => { setOpenCreate(false); setEditRow(null); }}
        onSubmit={editRow ? onUpdate : onCreate}
        loading={creating || updating}
        editData={editRow}
      />

      <EquipmentDetailModal
        isOpen={!!detailRow}
        onClose={() => setDetailRow(null)}
        data={detailRow}
      />

      <EquipmentQRModal
        isOpen={!!qrRow}
        onClose={() => setQrRow(null)}
        data={qrRow}
      />

      <EquipmentImportModal
        isOpen={openImport}
        onClose={() => setOpenImport(false)}
        branchId={selectedBranchId}
        onSuccess={() => void load()}
      />
    </div>
  );
};

export default EquipmentIndexPage;
